import Modal from "@/components/modal"
import { PhysicalRisk } from "@/entities/physical-risk/model/physical-risk.types"
import { formatRiskValue } from "@/entities/physical-risk/model/physical-risk.utils"
import PhysicalRiskPreventionFunnel from "@/entities/physical-risk/ui/PhysicalRiskPreventionFunnel"
import PhysicalRiskScale from "@/entities/physical-risk/ui/PhysicalRiskScale"
import { PhysicalRiskMeter } from "@/entities/physical-risk/ui/PhysicalRiskMeter"
import { AccountRisksSettings } from "@/redux/account.types"
import { t } from "@/translate/t"

interface Props {
  onClose: () => void
  accountRisksSettings: AccountRisksSettings
  risk: PhysicalRisk
}

//Build the accountRisksSettings's residual-risk → IDTree-note mapping, grouped by note:
//for each note, the min/max residual value that maps to it.
function getNormalizedRanges(cNormalizedValues: AccountRisksSettings["cNormalizedValues"]) {
  const byNote = new Map<number, { min: number; max: number }>()

  for (const item of (cNormalizedValues ?? []).flat()) {
    if (!item || typeof item.value !== "number" || typeof item.normalizedValue !== "number") continue
    const current = byNote.get(item.normalizedValue)
    if (!current) {
      byNote.set(item.normalizedValue, { min: item.value, max: item.value })
    } else {
      current.min = Math.min(current.min, item.value)
      current.max = Math.max(current.max, item.value)
    }
  }

  return Array.from(byNote.entries())
    .map(([note, range]) => ({ note, ...range }))
    .sort((a, b) => a.note - b.note)
}

function ResultBadge({ value, label, color }: { value: number | null; label?: string | null; color?: string | null }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-sm font-bold"
      style={{ color: color ?? "#334155", backgroundColor: color ? `${color}1a` : "#f1f5f9" }}>
      {value ?? "—"}
      {label && <span className="text-sm font-semibold opacity-80">{label}</span>}
    </span>
  )
}

export default function PhysicalRiskEvaluationHelpModal({ onClose, accountRisksSettings, risk }: Props) {
  const normalizedRanges = getNormalizedRanges(accountRisksSettings.cNormalizedValues)

  return (
    <Modal title="Comment est évalué chaque risque ?" isLarge isCloseButtonVisible onClose={onClose}>
      <div className="space-y-8 text-slate-700">
        {/* Intro */}
        <div className="space-y-3">
          <p>Chaque risque est évalué selon sa gravité, sa fréquence d&apos;exposition et sa prévention.</p>
          <p className="text-sm grey-t">
            La prévention réduit le risque : plus sa valeur est élevée, plus les moyens de maîtrise sont considérés
            efficaces.
          </p>
        </div>

        {/* Calcul */}
        <div className="space-y-3 rounded-[12px] border border-slate-200 bg-slate-50 p-5">
          {/* Le bloc presente le brut ET le residuel : pas le titre "Calcul du risque brut" de la carte */}
          <div className="text-sm font-semibold uppercase tracking-wide grey-t">Calcul de la note</div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-semibold text-slate-800">{t("physical_risk_calculation_gross_risk")}</span>
            <span className="text-sm grey-t">
              = {t("physical_risk_calculation_severity")} × {t("physical_risk_calculation_frequency")} =
            </span>
            <ResultBadge value={risk.cValueInitial} label={risk.cValueInitialLabel} color={risk.cValueInitialColor} />
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-semibold text-slate-800">Risque résiduel</span>
            <span className="text-sm grey-t">= risque brut ÷ prévention =</span>
            <ResultBadge
              value={risk.cValueEffective}
              label={risk.cValueEffectiveLabel}
              color={risk.cValueEffectiveColor}
            />
          </div>
        </div>

        {/* Échelles */}
        <div className="grid grid-cols-2 items-start gap-40">
          <div>
            <div className="mb-20 text-lg">Gravité</div>
            <PhysicalRiskScale id="severity" isReadonly risk={risk} />
          </div>
          <div>
            <div className="mb-20 text-lg">Fréquence</div>
            <PhysicalRiskScale id="frequency" isReadonly risk={risk} />
          </div>
          <div className="col-span-2">
            <div className="mb-20 text-lg">Prévention</div>
            <PhysicalRiskPreventionFunnel isReadonly risk={risk} />
          </div>
        </div>

        {/* Note IDTree */}
        {!accountRisksSettings.isIdtreeScoreHidden && (
          <div className="space-y-3 mt-10">
            <div className="text-lg">La note IDTree</div>
            <div className="flex items-center gap-20">
              <div className="shrink-0">
                <PhysicalRiskMeter risk={risk} size="lg" />
              </div>
              <p>
                C&apos;est la note affichée dans la jauge. Le <b>risque résiduel</b> est converti en une note commune à
                tous les risques, selon le barème défini pour votre organisation, afin d&apos;être plus clair pour
                l&apos;utilisateur.
              </p>
            </div>

            {normalizedRanges.length > 0 && (
              <div
                className="overflow-hidden rounded-[12px] bg-white text-sm mt-10"
                style={{ border: "1px solid #E2E8F0" }}>
                {normalizedRanges.map((range) => {
                  const isCurrent = risk.cValueNormalized === range.note
                  return (
                    <div
                      key={range.note}
                      className="flex items-center justify-between gap-3 border-b border-slate-100 px-3 py-2 last:border-b-0"
                      style={isCurrent ? { backgroundColor: "#F1F5F9" } : undefined}>
                      <span className={isCurrent ? "font-semibold text-slate-900" : "text-slate-600"}>
                        Risque résiduel{" "}
                        {range.min === range.max ? (
                          <>de {formatRiskValue(range.min)}</>
                        ) : (
                          <>
                            de {formatRiskValue(range.min)} à {formatRiskValue(range.max)}
                          </>
                        )}
                      </span>
                      <span className="flex items-center gap-2 whitespace-nowrap">
                        <span className="grey-t">→</span>
                        <span
                          className="inline-flex items-center rounded-full px-2.5 py-0.5 font-bold"
                          style={
                            isCurrent
                              ? { backgroundColor: risk.cValueEffectiveColor ?? "#64748B", color: "white" }
                              : { backgroundColor: "#f1f5f9", color: "#475569" }
                          }>
                          note {range.note}
                        </span>
                      </span>
                    </div>
                  )
                })}
              </div>
            )}

            {risk.cValueNormalized != null && (
              <p>
                Ce risque a un risque résiduel de{" "}
                <b className="text-slate-900">{formatRiskValue(risk.cValueEffective ?? 0)}</b>, soit une note IDTree de{" "}
                <b className="text-slate-900">{risk.cValueNormalized}</b>.
              </p>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}
